import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';

import {
  AnnouncementPanel,
  Grounder,
  HeadingSection,
} from '@innovamat/glimmer-components';

import {
  AddClassroomModal,
  ERROR_TYPE,
  ErrorPage,
  useNavigation,
} from '../../..';
import { useManageClassrooms } from '../../hooks/use-manage-classrooms';

import { ConfirmationModal } from '../../components/confirmation-modal';

import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';

import {
  SchoolClassroomsEmptyState,
  SchoolClassroomsHeader,
  SchoolClassroomsSkeleton,
  StageClassroomsList,
} from './components';
import {
  useSchoolClassroomCreateOptions,
  useSchoolClassroomsData,
} from './hooks';
import { SchoolClassroomsTabs } from './school-classrooms-tabs';

export const ListContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const CourseBlock = styled('div')<{ isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  ${({ isDisabled }) =>
    isDisabled &&
    `
    pointer-events: none;
    opacity: 0.5;
    filter: grayscale(1);
  `}
`;

export const ClassroomGrid = styled.div`
  display: grid;
  gap: 1rem;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const EmptyStateWrapper = styled.div`
  margin-top: 3.5rem;
`;

const UncheckedClassesAfterYearUpgradeAnnouncementPanel = styled(
  AnnouncementPanel
)`
  margin-top: 1rem;
`;

function SchoolClassrooms(): JSX.Element {
  const { t } = useTranslation();

  const {
    handleDeleteClassWithConfirmation,
    confirmModalData,
    editModalData,
    openEditModal,
  } = useManageClassrooms();

  const { goToSchoolClassroom } = useNavigation();

  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const {
    groupedCourses,
    hasUncheckedClasses,
    isEmpty,
    isError,
    isLoading,
    isSuccess,
    selectedTab,
    getClassroomsByCourse,
    getYearUpdateCourse,
    tabs,
  } = useSchoolClassroomsData();

  const { createClassroomOptions } = useSchoolClassroomCreateOptions({
    openEditModal,
    handleDeleteClassWithConfirmation,
  });

  const handleNavigateToClassroom = (classroomId: string): void => {
    goToSchoolClassroom(classroomId);
  };

  return (
    <>
      <HeadingSection
        title={t('sidebar.items.classrooms-list')}
        actions={<SchoolClassroomsHeader />}
      />
      <SchoolClassroomsTabs tabs={tabs} />
      {hasUncheckedClasses && isSuccess && !isUpdatePeriod && (
        <UncheckedClassesAfterYearUpgradeAnnouncementPanel
          text={t('classrooms.updatePeriod.announcement')}
          canClose={false}
          type="imported"
        />
      )}

      <UpdatePeriodAnnouncementPanel />

      <ListContainer>
        {isLoading && <SchoolClassroomsSkeleton />}
        {isError && <ErrorPage errorType={ERROR_TYPE.ERROR_500} />}
        {isEmpty && (
          <EmptyStateWrapper>
            <SchoolClassroomsEmptyState />
          </EmptyStateWrapper>
        )}
        <StageClassroomsList
          groupedCourses={groupedCourses}
          selectedTab={selectedTab}
          isUpdatePeriod={isUpdatePeriod}
          getClassroomsByCourse={getClassroomsByCourse}
          createClassroomOptions={createClassroomOptions}
          onNavigateToClassroom={handleNavigateToClassroom}
        />
        <Grounder />
      </ListContainer>
      <ConfirmationModal {...confirmModalData} />
      <AddClassroomModal {...editModalData} />
    </>
  );
}

export { SchoolClassrooms };
